import { Search } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { stripInline } from "@/components/docs/inline";
import { allDocPages, docHref } from "@/content/docs/index";
import { blockText } from "@/content/docs/types";
import { cn } from "@/lib/cn";

const MAX_RESULTS = 8;

type Indexed = { slug: string; title: string; description: string; haystack: string };

/**
 * Client-side documentation search.
 *
 * The index covers every block's text. The previous implementation read only
 * `block.text`, which silently excluded all list, table, step, and FAQ
 * content — roughly half the corpus was unsearchable, and the half it dropped
 * was the half carrying the specifics.
 */
function buildIndex(): Indexed[] {
  return allDocPages().map((page) => ({
    slug: page.slug,
    title: page.title,
    description: page.description,
    haystack: stripInline(
      [page.title, page.description, ...(page.targets ?? []), ...page.blocks.map(blockText)].join(
        " ",
      ),
    ).toLowerCase(),
  }));
}

export function DocsSearch() {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const index = useMemo(buildIndex, []);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length < 2) return [];
    // Every whitespace-separated term must appear, so "alpaca paper" narrows
    // instead of returning every page mentioning either word.
    const terms = needle.split(/\s+/);
    return index
      .filter((entry) => terms.every((term) => entry.haystack.includes(term)))
      .sort((a, b) => score(b, terms) - score(a, terms))
      .slice(0, MAX_RESULTS);
  }, [query, index]);

  const expanded = open && results.length > 0;

  function commit(slug: string) {
    setQuery("");
    setOpen(false);
    navigate(docHref(slug));
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!expanded) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      const hit = results[active];
      if (hit) {
        event.preventDefault();
        commit(hit.slug);
      }
    }
  }

  const showNoResults = open && query.trim().length >= 2 && results.length === 0;

  return (
    <search className="relative" aria-label="Documentation">
      <label htmlFor={`${listId}-input`} className="sr-only">
        Search documentation
      </label>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle-foreground"
        aria-hidden
      />
      <input
        id={`${listId}-input`}
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        aria-activedescendant={expanded ? `${listId}-${active}` : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Blur is deferred so a click on a result lands before the list closes.
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
        placeholder="Search docs…"
        className="w-full rounded-md border border-input bg-card py-2.5 pl-10 pr-3 text-base text-foreground placeholder:text-subtle-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/40 lg:text-sm"
      />

      <p aria-live="polite" className="sr-only">
        {query.trim().length < 2
          ? ""
          : `${results.length} ${results.length === 1 ? "result" : "results"}`}
      </p>

      {expanded ? (
        <ul
          id={listId}
          // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: listbox is the required popup role for the ARIA combobox pattern
          role="listbox"
          aria-label="Search results"
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-border bg-card py-1 shadow-editorial"
        >
          {results.map((hit, i) => (
            <li
              key={hit.slug}
              id={`${listId}-${i}`}
              // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: option is the required child role inside a listbox
              role="option"
              // Programmatically focusable but never a tab stop: focus stays in
              // the input and selection travels via aria-activedescendant.
              tabIndex={-1}
              aria-selected={i === active}
              className={cn("cursor-pointer px-3 py-2", i === active && "bg-muted")}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                // mousedown fires before blur, so the navigation is not lost.
                e.preventDefault();
                commit(hit.slug);
              }}
            >
              <span className="block text-sm font-medium text-foreground">{hit.title}</span>
              <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                {hit.description}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {showNoResults ? (
        <p className="absolute z-20 mt-1 w-full rounded-md border border-border bg-card px-3 py-3 text-sm text-muted-foreground shadow-editorial">
          No matching pages
        </p>
      ) : null}
    </search>
  );
}

/** Title hits outrank body hits; an exact title match outranks both. */
function score(entry: Indexed, terms: string[]): number {
  const title = entry.title.toLowerCase();
  let total = 0;
  for (const term of terms) {
    if (title === term) total += 10;
    if (title.includes(term)) total += 4;
    if (entry.description.toLowerCase().includes(term)) total += 2;
  }
  return total;
}
